import { EventEmitter, Injectable } from "@angular/core";
import { catchError, finalize, map, mergeMap, takeUntil } from "rxjs/operators";
import { SHOW_SNACKBAR } from "../reducers/actions/snackbar";
import { SnackbarConfig } from "./snackbar-config";
import { Statement, TaxAuthority } from "../statements";
import { Store } from "@ngrx/store";
import { State } from "../reducers";
import { StatementService } from "../statements/statement.service";
import { SubscribingComponent } from "./subscribing-component";
import { Employer } from "../statements/employer";
import { UPDATE_REFUND_DETAILS } from "../reducers/actions/refund-details";
import { Observable, of as observableOf, Subject } from "rxjs";
import { LohnsteuerValidator } from "../statements/lohnsteuerValidator";
import { ArrayHelper } from "../core/helpers/array.helper";
import { ErrorMessage } from "app/core/form-validator/validatorInterface";
import { ValidateFieldService } from "app/core/form-validator/validate-field.service";
import { VALIDATION_FIELD_KEYS_FOR_EMPLOYER } from "app/core/form-validator/validtorField";
import { ValidateFieldV2Service } from "app/core/form-validator/validate-field-v2.service";
import { SingleRowCollapseType } from "./tax-card-single-row/tax-card-single-row.component";
import { RegExValidator, ValidatorErrorMessage } from "app/core/form-validator/validatorRules";

@Injectable({
  providedIn: "root"
})
export class TaxCardIncomeInfoService {
  public uploadingScan = false;
  public loading = false;
  public scanUploaded = false;
  public scanWasSuccessfullyUploaded = false;
  public mainStatement: Statement;
  public currentStatement: Statement;
  public currentEmployer: Employer;
  public onAddEmployerCardEmitter = new EventEmitter();
  public onSuccessfullyUpdateEmployerEmitter = new EventEmitter();
  public removeEmployerCallbackEmitter = new EventEmitter();
  public enableScanUpload = false;
  public formsError = [];
  public employerHasErrors = [];
  public errors = [];
  public disableOnProceed = false;
  public addTaxCardToStatement = null;
  public validatorIndex = 0;
  public defaultTaxClass = 0;
  public epValidator: ErrorMessage[] = [];
  public empWarningList: LohnsteuerValidator[] = [];
  private ngUnsubscribe: Subject<void>;

  constructor(
    private store: Store<State>,
    private statementService: StatementService,
    private validatorService: ValidateFieldService
  ) {}

  public _init(_mainStatement: Statement, _currentStatement: Statement = null, _ngUnsubscribe: Subject<void>): void {
    if (_mainStatement) {
      this.mainStatement = _mainStatement;
    }
    if (_currentStatement) {
      this.currentStatement = _currentStatement;
    } else if (_mainStatement) {
      this.currentStatement = _mainStatement;
    }

    this.ngUnsubscribe = _ngUnsubscribe;
  }

  public get _isInitialized(): boolean {
    return !!this.mainStatement && !!this.currentStatement;
  }

  public get isCurrentEmployerHasId(): boolean {
    return this.currentEmployer && !!this.currentEmployer.id;
  }

  public isStatementYearLowerThan2025(): boolean {
    return this.currentStatement.fullYear < 2025;
  }

  public formValidationErrors(): void {
    console.log(this.currentStatement.fullYear);

    this.epValidator = [];
    if (!this.currentStatement) {
      return;
    }

    this.validatorService.currentYear = this.mainStatement.fullYear;
    let i = 0;

    this.currentStatement.employers.forEach((employer: Employer) => {
      this.validatorService.clearErrorMessages();
      this.validatorService.index = i;

      this.epValidator = [
        ...this.epValidator,
        ...this.validatorService.validateFieldAndGetMessage(employer, VALIDATION_FIELD_KEYS_FOR_EMPLOYER)
      ];

      //FIELD NUMBER 8 VALIDATION RULES
      if (employer.pension_benefits) {
        if (employer.pension_benefits === "0") {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "pension_benefits",
              ValidatorErrorMessage.ZERO_IS_NOT_ALLOWED
            )
          );
        } else if (!RegExValidator.FIELD_IS_INTEGER_NEGATIVE_12CHAR.test(employer.pension_benefits)) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "pension_benefits",
              ValidatorErrorMessage.FIELD_IS_INTEGER_NEGATIVE_12CHAR
            )
          );
        } else if (parseFloat(employer.ib03) < parseFloat(employer.pension_benefits) || !employer.ib03) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage("pension_benefits", "PENSION_BENEFITS_VS_IB03")
          );
        } else {
          if (employer.assessment_basis_for_pension === null || employer.assessment_basis_for_pension === "") {
            this.epValidator.push(
              ValidateFieldV2Service.addDateEpValidatorMessage(
                "assessment_basis_for_pension",
                ValidatorErrorMessage.FIELD_IS_REQUIRED
              )
            );

            if (!employer[SingleRowCollapseType.useAdditionalCartFrom29To32]) {
              this.epValidator.push(
                ValidateFieldV2Service.addDateEpValidatorMessage(
                  SingleRowCollapseType.useAdditionalCartFrom29To32,
                  "FILL_THIS_SECTION"
                )
              );
            }
          }

          if (
            employer.pension_benefits_commencement_year === null ||
            employer.pension_benefits_commencement_year === ""
          ) {
            this.epValidator.push(
              ValidateFieldV2Service.addDateEpValidatorMessage(
                "pension_benefits_commencement_year",
                ValidatorErrorMessage.FIELD_IS_REQUIRED
              )
            );

            if (!employer[SingleRowCollapseType.useAdditionalCartFrom29To32]) {
              this.epValidator.push(
                ValidateFieldV2Service.addDateEpValidatorMessage(
                  SingleRowCollapseType.useAdditionalCartFrom29To32,
                  "FILL_THIS_SECTION"
                )
              );
            }
          }
        }
      }

      //FIELD NUMBER 29 VALIDATION RULES
      if (employer.assessment_basis_for_pension) {
        if (!RegExValidator.FIELD_IS_INTEGER_NEGATIVE_12CHAR.test(employer.assessment_basis_for_pension)) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "assessment_basis_for_pension",
              ValidatorErrorMessage.FIELD_IS_INTEGER_NEGATIVE_12CHAR
            )
          );
        } else if (employer.pension_benefits === null || employer.pension_benefits === "") {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "pension_benefits",
              ValidatorErrorMessage.FIELD_IS_REQUIRED
            )
          );
          if (!employer[SingleRowCollapseType.useAdditionalCartFrom8To14]) {
            this.epValidator.push(
              ValidateFieldV2Service.addDateEpValidatorMessage(
                SingleRowCollapseType.useAdditionalCartFrom8To14,
                "FILL_THIS_SECTION"
              )
            );
          }
        }
      }

      //FIELD NUMBER 30 VALIDATION RULES
      if (employer.pension_benefits_commencement_year) {
        if (!RegExValidator.YEAR_FORMAT.test(employer.pension_benefits_commencement_year)) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "pension_benefits_commencement_year",
              ValidatorErrorMessage.YEAR_FORMAT
            )
          );
        } else if (parseInt(employer.pension_benefits_commencement_year) < 1950) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "pension_benefits_commencement_year",
              "PENSION_BENEFITS_COMMENCEMENT_YEAR"
            )
          );
        } else {
          if (
            (employer.pension_benefits === null || employer.pension_benefits === "") &&
            (employer.reduced_tax_pension_benefits === null || employer.reduced_tax_pension_benefits === "")
          ) {
            this.epValidator.push(
              ValidateFieldV2Service.addDateEpValidatorMessage("pension_benefits", "FIELD_8_OR_9_REQUIRED")
            );
            if (!employer[SingleRowCollapseType.useAdditionalCartFrom8To14]) {
              this.epValidator.push(
                ValidateFieldV2Service.addDateEpValidatorMessage(
                  SingleRowCollapseType.useAdditionalCartFrom8To14,
                  "FILL_THIS_SECTION"
                )
              );
            }
          }
        }
      }

      //FIELD NUMBER 31 VALIDATION RULES (its divided to pensionBenefitsPaymentsEnd and pensionBenefitsPaymentStart)
      if (employer.pensionBenefitsPaymentsEnd) {
        if (
          parseInt(employer.pensionBenefitsPaymentsEnd) <= 0 ||
          parseInt(employer.pensionBenefitsPaymentsEnd) > 12 ||
          !Number.isInteger(parseInt(employer.pensionBenefitsPaymentsEnd))
        ) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage("pensionBenefitsPaymentsEnd", "ALLOWED_1_12_RANGE")
          );
        } else if (!employer.pensionBenefitsPaymentsStart) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "pensionBenefitsPaymentsStart",
              ValidatorErrorMessage.FIELD_IS_NOT_ZERO
            )
          );
        } else if (parseInt(employer.pensionBenefitsPaymentsStart) > parseInt(employer.pensionBenefitsPaymentsEnd)) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "pensionBenefitsPaymentsEnd",
              "PENSION_BENEFITS_END_LESS_THAN_START"
            )
          );
        }
      }

      if (employer.pensionBenefitsPaymentsStart) {
        if (
          parseInt(employer.pensionBenefitsPaymentsStart) <= 0 ||
          parseInt(employer.pensionBenefitsPaymentsStart) > 12 ||
          !Number.isInteger(parseInt(employer.pensionBenefitsPaymentsStart))
        ) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage("pensionBenefitsPaymentsStart", "ALLOWED_1_12_RANGE")
          );
        } else if (!employer.pensionBenefitsPaymentsEnd) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "pensionBenefitsPaymentsEnd",
              ValidatorErrorMessage.FIELD_IS_NOT_ZERO
            )
          );
        } else if (parseInt(employer.pensionBenefitsPaymentsStart) > parseInt(employer.pensionBenefitsPaymentsStart)) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "pensionBenefitsPaymentsEnd",
              "PENSION_BENEFITS_START_BIGGER_THAN_END"
            )
          );
        }
      }

      if (
        employer.pensionBenefitsPaymentsStart &&
        employer.pensionBenefitsPaymentsEnd &&
        (employer.pension_benefits === null || employer.pension_benefits === "")
      ) {
        this.epValidator.push(
          ValidateFieldV2Service.addDateEpValidatorMessage("pension_benefits", ValidatorErrorMessage.FIELD_IS_NOT_ZERO)
        );

        if (!employer[SingleRowCollapseType.useAdditionalCartFrom8To14]) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              SingleRowCollapseType.useAdditionalCartFrom8To14,
              "FILL_THIS_SECTION"
            )
          );
        }
      }

      //FIELD NUMBER 32 VALIDATION RULES

      if (employer.funeral_benefits) {
        if (employer.funeral_benefits === "0") {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "funeral_benefits",
              ValidatorErrorMessage.ZERO_IS_NOT_ALLOWED
            )
          );
        } else if (!RegExValidator.FIELD_IS_INTEGER_NEGATIVE_12CHAR.test(employer.funeral_benefits)) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "funeral_benefits",
              ValidatorErrorMessage.FIELD_IS_INTEGER_NEGATIVE_12CHAR
            )
          );
        } else {
          if (employer.pension_benefits === null || employer.pension_benefits === "") {
            this.epValidator.push(
              ValidateFieldV2Service.addDateEpValidatorMessage(
                "pension_benefits",
                ValidatorErrorMessage.FIELD_IS_REQUIRED
              )
            );

            if (!employer[SingleRowCollapseType.useAdditionalCartFrom8To14]) {
              this.epValidator.push(
                ValidateFieldV2Service.addDateEpValidatorMessage(
                  SingleRowCollapseType.useAdditionalCartFrom8To14,
                  "FILL_THIS_SECTION"
                )
              );
            }
          } else if (parseFloat(employer.pension_benefits) <= parseFloat(employer.funeral_benefits)) {
            this.epValidator.push(
              ValidateFieldV2Service.addDateEpValidatorMessage(
                "funeral_benefits",
                "FUNREAL_BENEFITS_VS_PENSION_BENEFITS"
              )
            );
          }
        }
      }

      //FIELD NUMBER 9 VALIDATION RULES
      if (employer.reduced_tax_pension_benefits) {
        if (!RegExValidator.FIELD_IS_INTEGER_NEGATIVE_12CHAR.test(employer.reduced_tax_pension_benefits)) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "reduced_tax_pension_benefits",
              ValidatorErrorMessage.FIELD_IS_INTEGER_NEGATIVE_12CHAR
            )
          );
        } else {
          if (
            employer.pension_benefits_commencement_year === null ||
            employer.pension_benefits_commencement_year === ""
          ) {
            this.epValidator.push(
              ValidateFieldV2Service.addDateEpValidatorMessage(
                "pension_benefits_commencement_year",
                ValidatorErrorMessage.FIELD_IS_REQUIRED
              )
            );
          }

          if (
            !employer[SingleRowCollapseType.useAdditionalCartFrom29To32] &&
            (employer.pensionBenefitsPaymentsEnd === null ||
              employer.pensionBenefitsPaymentsEnd === "" ||
              employer.pensionBenefitsPaymentsStart === null ||
              employer.pensionBenefitsPaymentsStart === "")
          ) {
            this.epValidator.push(
              ValidateFieldV2Service.addDateEpValidatorMessage(
                SingleRowCollapseType.useAdditionalCartFrom29To32,
                "FILL_THIS_SECTION"
              )
            );
          }

          if (
            this.isStatementYearLowerThan2025() &&
            (employer.payroll_tax_withheld === null || employer.payroll_tax_withheld === "")
          ) {
            this.epValidator.push(
              ValidateFieldV2Service.addDateEpValidatorMessage(
                "payroll_tax_withheld",
                ValidatorErrorMessage.FIELD_IS_REQUIRED
              )
            );
          }
        }
      }

      //FIELD NUMBER 10 VALIDATION RULES
      if (employer.reduced_tax_wages) {
        if (!RegExValidator.FIELD_IS_INTEGER_NEGATIVE_12CHAR.test(employer.reduced_tax_wages)) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "reduced_tax_wages",
              ValidatorErrorMessage.FIELD_IS_INTEGER_NEGATIVE_12CHAR
            )
          );
        } else if (
          this.isStatementYearLowerThan2025() &&
          (employer.payroll_tax_withheld === null || employer.payroll_tax_withheld === "")
        ) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "payroll_tax_withheld",
              ValidatorErrorMessage.FIELD_IS_REQUIRED
            )
          );
        }
      }

      //FIELD NUMBER 19 VALIDATION RULES
      if (this.isStatementYearLowerThan2025() && employer.ib19 !== "") {
        if (parseFloat(employer.ib03) < parseFloat(employer.ib19) || (!employer.ib03 && employer.ib19)) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage("ib19", "WAGES_NOT_COVERED_BY_REDUCED_TAX_VS_IB03")
          );
        }
      }

      //FIELD NUMBER 11 VALIDATION RULES
      if (this.isStatementYearLowerThan2025() && employer.payroll_tax_withheld) {
        if (!RegExValidator.NUMBER_2_DECIMAl_ALLOW_NEGATIVE_12.test(employer.payroll_tax_withheld)) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "payroll_tax_withheld",
              ValidatorErrorMessage.FIELD_IS_DECIMAL_NEGATIVE_12CHAR
            )
          );
        } else if (!employer.reduced_tax_pension_benefits && !employer.reduced_tax_wages) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              SingleRowCollapseType.useAdditionalCartFrom8To14,
              "FIELD_9_OR_10_REQUIRED"
            )
          );
        }
      }

      //FIELD NUMBER 12 VALIDATION RULES
      if (this.isStatementYearLowerThan2025() && employer.solidarity_surcharge_withheld) {
        if (!RegExValidator.NUMBER_2_DECIMAl_ALLOW_NEGATIVE_12.test(employer.solidarity_surcharge_withheld)) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "solidarity_surcharge_withheld",
              ValidatorErrorMessage.FIELD_IS_DECIMAL_NEGATIVE_12CHAR
            )
          );
        } else if (!employer.reduced_tax_pension_benefits && !employer.reduced_tax_wages) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              SingleRowCollapseType.useAdditionalCartFrom8To14,
              "FIELD_9_OR_10_REQUIRED"
            )
          );
        }
      }

      //FIELD NUMBER 13 VALIDATION RULES
      if (this.isStatementYearLowerThan2025() && employer.retained_church_tax_withheld) {
        if (!RegExValidator.NUMBER_2_DECIMAl_ALLOW_NEGATIVE_12.test(employer.retained_church_tax_withheld)) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "retained_church_tax_withheld",
              ValidatorErrorMessage.FIELD_IS_DECIMAL_NEGATIVE_12CHAR
            )
          );
        } else if (!employer.reduced_tax_pension_benefits && !employer.reduced_tax_wages) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              SingleRowCollapseType.useAdditionalCartFrom8To14,
              "FIELD_9_OR_10_REQUIRED"
            )
          );
        }
      }

      //FIELD NUMBER 14 VALIDATION RULES
      if (this.isStatementYearLowerThan2025() && employer.spouse_retained_church_tax_withheld) {
        if (!RegExValidator.NUMBER_2_DECIMAl_ALLOW_NEGATIVE_12.test(employer.spouse_retained_church_tax_withheld)) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              "spouse_retained_church_tax_withheld",
              ValidatorErrorMessage.FIELD_IS_DECIMAL_NEGATIVE_12CHAR
            )
          );
        } else if (!employer.reduced_tax_pension_benefits && !employer.reduced_tax_wages) {
          this.epValidator.push(
            ValidateFieldV2Service.addDateEpValidatorMessage(
              SingleRowCollapseType.useAdditionalCartFrom8To14,
              "FIELD_9_OR_10_REQUIRED"
            )
          );
        }
      }
      i++;
    });

    this.epValidator = Array.from(new Map(this.epValidator.map((m) => [JSON.stringify(m), m])).values());
  }

  public formValidationWarnings(): void {
    this.empWarningList = [];

    this.currentStatement.employers.forEach((employer, index) => {
      this.empWarningList.push(new LohnsteuerValidator(employer, index));
    });
  }

  public setPeriodOfEmployment(): void {
    this.currentStatement.employers.forEach((item: Employer) => {
      item.formatPeriodOfEmployment(this.mainStatement.fullYear);
    });
  }

  public setPensionBenefitsPayments(): void {
    this.currentStatement.employers.forEach((item: Employer) => {
      item.formatPensionBenefitsPayments();
    });
  }

  public selectHasEmployerCard(hasEmployerCard: boolean) {
    this.currentStatement.hasEmployers = hasEmployerCard;
    this.addTaxCardToStatement = hasEmployerCard;
    this.currentStatement.has_employers_selected = hasEmployerCard;
    if (!this.currentStatement.hasEmployers) {
      this.currentStatement.employers = [];
    }
  }

  public updateEmployer(showSnackbar = false): void {
    this.loading = true;
    this.disableOnProceed = true;

    this.formsError = [];

    const id = this.currentStatement.id;
    const payload = this.currentStatement.employers.map((employer) => employer.toResponse(null));

    const handleResponse = (response: Employer[]) => {
      this.currentStatement.employers = [...response];
      this.onDataUpdateSuccess(true, showSnackbar);
      this.currentEmployer = this.currentStatement.employers[this.validatorIndex];

      this.onSuccessfullyUpdateEmployerEmitter.emit();
    };

    if (id) {
      this.statementService
        .updateEmployers(id, payload)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          handleResponse,
          (errors: any) => this.handleErrors(errors.error),
          () => {
            this.disableOnProceed = false;
          }
        );
    }
  }

  public deleteSelectedEmployer(_index: number): void {
    if (this.currentStatement?.employers[_index]?.id) {
      this.removeEmployer(_index);
    } else {
      this.onRemoveEmployerSuccess(_index);
    }
  }

  public removeEmployer(index): void {
    this.mainStatement.deleting = true;
    const currentEmployer = this.currentStatement.employers[index];
    if (currentEmployer) {
      if (currentEmployer.id) {
        this.loading = true;
        this.disableOnProceed = true;

        this.statementService
          .deleteEmployer(this.currentStatement.id, currentEmployer.id)
          .pipe(
            takeUntil(this.ngUnsubscribe),
            finalize(() => (this.mainStatement.deleting = false))
          )
          .subscribe(
            () => {
              this.store.dispatch({
                type: SHOW_SNACKBAR,
                payload: new SnackbarConfig("SNACKBAR.EMPLOYER_DELETED")
              });

              this.onRemoveEmployerSuccess(index);
            },
            (errors: any) => {
              this.errors = errors.error;
            },
            () => {
              this.loading = false;
              this.disableOnProceed = false;
            }
          );
      } else {
        this.store.dispatch({
          type: SHOW_SNACKBAR,
          payload: new SnackbarConfig("SNACKBAR.EMPLOYER_DELETED")
        });

        this.onRemoveEmployerSuccess(index);
      }
    }
  }

  public handleErrors(error: any[]): void {
    this.disableOnProceed = false;
    this.errors = error;
    this.setFormErrors();
  }

  public clearErrors(): void {
    this.errors = [];
    this.employerHasErrors = [];
  }

  public onDataUpdateSuccess(updateRefund = false, showSnackbar = true): void {
    this.clearErrors();

    if (showSnackbar) {
      this.store.dispatch({
        type: SHOW_SNACKBAR,
        payload: new SnackbarConfig("SNACKBAR.EMPLOYER_UPDATED")
      });
    }

    if (updateRefund) {
      this.store.dispatch({ type: UPDATE_REFUND_DETAILS });
    }
  }

  public addNewEmployerCard(): void {
    this.formsError = [];

    const lastIndex = this.currentStatement.employers.length - 1;

    if (lastIndex >= 0 && this.currentStatement.employers[lastIndex].isEmpty()) {
      this.validatorIndex = lastIndex;
      this.currentEmployer = this.currentStatement.employers[lastIndex];

      this.store.dispatch({
        type: SHOW_SNACKBAR,
        payload: new SnackbarConfig("SNACKBAR.LAST_EMPLOYER_IS_EMPTY")
      });

      return;
    }

    const newEmployer = new Employer();
    newEmployer.taxClass = this.defaultTaxClass;
    if (this.currentStatement.taxClass) {
      newEmployer.taxClass = this.mainStatement.taxClass;
    }
    this.currentStatement.employers.push(newEmployer);
    this.validatorIndex = this.currentStatement.employers.length - 1;
    this.currentEmployer = newEmployer;
    this.currentStatement.hasEmployers = true;

    this.onAddEmployerCardEmitter.emit();
  }

  public updateHasEmployers(isSpouse = false): Observable<Statement> {
    let data;

    if (!isSpouse) {
      data = this.mainStatement.hasEmployersToResponse();
    } else {
      data = this.mainStatement.spouseHasEmployersToResponse;
    }

    return this.statementService.updateStatement(this.mainStatement.id, data).pipe(
      mergeMap((responseStatement) => {
        const employers = [...this.currentStatement.employers];
        const notExistsEmployerIds = [];
        let employersToCompare = [];
        if (
          this.currentStatement.isSpouse() &&
          this.currentStatement.employers.length !== responseStatement.spouse.employers.length
        ) {
          employersToCompare = [...responseStatement.spouse.employers];
        } else if (this.currentStatement.employers.length !== responseStatement.employers.length) {
          employersToCompare = [...responseStatement.employers];
        }

        employersToCompare.some((employer) => {
          if (!this.currentStatement.employers.find((x) => x.id === employer.id)) {
            notExistsEmployerIds.push(employer.id);
          }
        });

        if (this.currentStatement.isSpouse()) {
          responseStatement.spouse.employers = employers;
        } else {
          responseStatement.employers = employers;
        }

        if (notExistsEmployerIds.length) {
          return this.statementService.deleteMultipleEmployers(this.currentStatement.id, notExistsEmployerIds).pipe(
            map(() => responseStatement),
            catchError(() => observableOf(responseStatement))
          );
        }

        return observableOf(responseStatement);
      })
    );
  }

  private onRemoveEmployerSuccess(index: number): void {
    this.currentStatement.employers.splice(index, 1);

    if (!this.currentStatement.employers.length) {
      this.addTaxCardToStatement = null;
      this.validatorIndex = 0;
      this.currentEmployer = null;
      this.currentStatement.hasEmployers = false;
      this.currentStatement.has_employers_selected = null;
    }

    this.removeEmployerCallbackEmitter.emit();
  }

  private setFormErrors(): any {
    this.formsError = [];
    this.employerHasErrors = [];
    let index = 0;
    if (this.currentStatement) {
      if (this.currentStatement.employers && this.errors) {
        this.currentStatement.employers.forEach((emp) => {
          if (ArrayHelper.isArrayAndNotEmpty(this.errors)) {
            const err = this.errors.filter((er) => er.index === index)[0];
            this.formsError[index] = err ? err : [];

            if (err) {
              this.employerHasErrors.push(true);
            } else {
              this.employerHasErrors.push(false);
            }
          }
          index++;
        });
      }
    }
  }
}
